import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getDeals, createDeal, updateDeal, deleteDeal } from '../services/api';

const stageBadge = s => ({
  activated:'badge badge-green', onboarded:'badge badge-blue',
  onboarding:'badge badge-amber', engaged:'badge badge-blue',
  lead:'badge badge-gray', outreach:'badge badge-gray',
})[s?.toLowerCase()] || 'badge badge-gray';

const STAGES = ['lead','outreach','engaged','demo_scheduled','onboarding','onboarded','activated'];

export default function Deals({ toast }) {
  const { token } = useApp();
  const [deals, setDeals]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [creating, setCreating] = useState(false);
  const [newDeal, setNewDeal]   = useState({ name:'', stage:'lead', amount:'', closeDate:'', owner:'', notes:'' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getDeals(token);
      setDeals(res.data || []);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const handleCreate = async () => {
    if (!newDeal.name.trim()) { toast?.('Enter a deal name', 'error'); return; }
    try {
      await createDeal(token, { ...newDeal, amount: parseFloat(newDeal.amount) || 0 });
      toast?.('Deal created');
      setCreating(false);
      setNewDeal({ name:'', stage:'lead', amount:'', closeDate:'', owner:'', notes:'' });
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDeal(token, id);
      toast?.('Deal deleted');
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const filtered = deals.filter(d => !search || d.name?.toLowerCase().includes(search.toLowerCase()));
  const totalValue = deals.reduce((s,d) => s + (parseFloat(d.amount) || 0), 0);
  const wonDeals   = deals.filter(d => d.stage === 'activated');
  const wonValue   = wonDeals.reduce((s,d) => s + (parseFloat(d.amount) || 0), 0);

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Pipeline value</div><div className="stat-value">${totalValue.toLocaleString()}</div><div className="stat-sub">{deals.length} deals</div></div>
        <div className="stat-card"><div className="stat-label">Won value</div><div className="stat-value">${wonValue.toLocaleString()}</div><div className="stat-sub stat-up">{wonDeals.length} closed</div></div>
        <div className="stat-card"><div className="stat-label">Avg deal size</div><div className="stat-value">${deals.length ? Math.round(totalValue/deals.length).toLocaleString() : 0}</div><div className="stat-sub">Per deal</div></div>
        <div className="stat-card"><div className="stat-label">Win rate</div><div className="stat-value">{deals.length ? Math.round((wonDeals.length/deals.length)*100) : 0}%</div><div className="stat-sub stat-up">Of all deals</div></div>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <input className="search-input" placeholder="Search deals…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex:1 }} />
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New deal</button>
      </div>

      {creating && (
        <div className="card" style={{ marginBottom:16, padding:'16px 20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
            <input className="search-input" placeholder="Deal name *" value={newDeal.name} onChange={e => setNewDeal(p=>({...p,name:e.target.value}))} />
            <input className="search-input" placeholder="Amount ($)" type="number" value={newDeal.amount} onChange={e => setNewDeal(p=>({...p,amount:e.target.value}))} />
            <select value={newDeal.stage} onChange={e => setNewDeal(p=>({...p,stage:e.target.value}))}
              style={{ background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }}>
              {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
            <input type="date" value={newDeal.closeDate} onChange={e => setNewDeal(p=>({...p,closeDate:e.target.value}))}
              style={{ background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text-primary)', fontSize:13, padding:'8px 10px' }} />
            <input className="search-input" placeholder="Owner" value={newDeal.owner} onChange={e => setNewDeal(p=>({...p,owner:e.target.value}))} />
            <input className="search-input" placeholder="Notes" value={newDeal.notes} onChange={e => setNewDeal(p=>({...p,notes:e.target.value}))} />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary" onClick={handleCreate}>Create deal</button>
            <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading deals…</div>
      ) : (
        <div className="card" style={{ padding:0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Deal</th><th>Stage</th><th>Amount</th><th>Close date</th><th>Owner</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>No deals yet — create your first deal</td></tr>
                ) : filtered.map(d => (
                  <tr key={d.id}>
                    <td><div style={{ fontWeight:500, color:'var(--text-primary)' }}>{d.name}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{d.notes}</div></td>
                    <td><span className={stageBadge(d.stage)}>{d.stage?.replace(/_/g,' ')}</span></td>
                    <td style={{ color:'var(--accent)', fontWeight:600 }}>${(parseFloat(d.amount)||0).toLocaleString()}</td>
                    <td style={{ color:'var(--text-dim)', fontSize:12 }}>{d.closeDate ? new Date(d.closeDate).toLocaleDateString() : '—'}</td>
                    <td style={{ color:'var(--text-muted)', fontSize:12 }}>{d.owner || '—'}</td>
                    <td><button className="btn btn-ghost" style={{ fontSize:11, color:'var(--danger)' }} onClick={() => handleDelete(d.id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
